import { useToasts } from "@certn/cascadia";
import { Layout, Loader } from "@certn/cascadia-react";
import { APIError, useAPIRequest } from "@certn/legacy-api";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";

import { AppFooter } from "../components/AppFooter";
import { CheckFlowHeader } from "../components/CheckFlowHeader";
import { ApplicationProvider } from "../pages/application/Application.context";
import { ApplicationPage } from "../pages/application/ApplicationPage";
import {
  CheckFlowNavbar,
  useNavbar,
} from "../pages/application/CheckFlowNavbar";

export function CheckFlowRoutes() {
  const starting = useRef(false);

  const { t } = useTranslation();
  const navigate = useNavigate();
  const { applicantId } = useParams();
  const request = useAPIRequest();
  const { show } = useToasts();

  const { canToggle } = useNavbar();

  const [menuOpen, setMenuOpen] = useState<boolean>(!canToggle);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!canToggle) {
      setMenuOpen(true);
      return;
    }
    setMenuOpen(false);
  }, [canToggle]);

  const startApplication = async (applicantId: string) => {
    if (starting.current) {
      return;
    }

    try {
      starting.current = true;
      setLoading(true);
      await request(`/internal/people/applicant/${applicantId}/start/`, {
        method: "POST",
      });
    } catch (e) {
      if (e instanceof APIError && e.status === 400) {
        // If the appliction is already started, we get a 400 error.
        return;
      }
      console.error("Error starting application:", e);
      show({
        title: t("Something went wrong, try again"),
        type: "error",
      });
      navigate("/");
    } finally {
      starting.current = false;
      setLoading(false);
    }
  };

  useEffect(() => {
    if (applicantId) {
      startApplication(applicantId);
    } else {
      navigate("/");
    }
  }, [applicantId]);

  if (loading) {
    return <Loader overlay />;
  }

  return (
    <ApplicationProvider>
      <Layout
        layout="applicant/narrow"
        header={
          <CheckFlowHeader onMenuToggle={() => setMenuOpen((prev) => !prev)} />
        }
        footer={<AppFooter />}
        sidebar={
          menuOpen && <CheckFlowNavbar onClose={() => setMenuOpen(false)} />
        }
      >
        <ApplicationPage />
      </Layout>
    </ApplicationProvider>
  );
}
